import { NgClass } from '@angular/common';
import { Component, effect, inject, signal, untracked, viewChild } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPopover,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';
import { VxErrorBarComponent } from '@libs/core/lib/error-view/error-bar.component';
import { NgEventBus } from '@libs/core/lib/event-bus/ng-event-bus';
import { VxSubmitButtonComponent } from '@libs/core/lib/form-controls/mtjr-submit-button.component';
import { VxCurrencyPipe } from '@libs/core/lib/pipes/mtjr-currency.pipe';
import { initialState } from '@libs/core/lib/state/async-states';
import { VxProductReviewsSectionComponent } from '@libs/mtjr/shared/products/product_reviews/mtjr-product-reviews-section.component';

import { DecimalPipe } from '@angular/common';
import { BASE_CONFIG } from '@libs/core/base-config';
import { AppAlerts } from '@libs/core/lib/utils/app-alerts';
import {
  withRequestToPublishProduct,
  withUnPublishProduct,
} from '@libs/mtjr/shared/generated/entities/product/product-store-features';
import { Entity } from '@libs/mtjr/shared/generated/types';
import { NotificationsStore } from '@libs/mtjr/shared/notifications/notifications.store';
import { VxProductAttributesSectionComponent } from '@libs/mtjr/shared/products/product_details/mtjr-product-attributes-section.component';
import { ProductDetailsStore } from '@libs/mtjr/shared/products/product_details/product-details.store';
import { VxProductSwiperComponent } from '@libs/mtjr/shared/products/product_details/product_swiper.component';
import { patchState, signalStore, withMethods } from '@ngrx/signals';
import { TranslatePipe } from '@ngx-translate/core';

export const ProductPublishStore = signalStore(
  withUnPublishProduct(),
  withRequestToPublishProduct(),
  withMethods((store) => ({
    resetRequestToPublishProductState() {
      patchState(store, { requestToPublishProductState: initialState() });
    },
    resetUnPublishProductState() {
      patchState(store, { unPublishProductState: initialState() });
    },
  })),
);

@Component({
  selector: 'mav-product-details',
  providers: [ProductDetailsStore, ProductPublishStore],
  imports: [
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButton,
    IonButtons,
    RouterLink,
    IonIcon,
    IonBackButton,
    IonContent,
    NgClass,
    VxSubmitButtonComponent,
    VxCurrencyPipe,
    VxErrorBarComponent,
    TranslatePipe,
    DecimalPipe,
    VxProductSwiperComponent,
    VxProductAttributesSectionComponent,
    IonText,
    IonFooter,
    VxProductReviewsSectionComponent,
    IonModal,
    IonPopover,
    IonList,
    IonItem,
    IonLabel,
  ],
  templateUrl: './product-details.page.html',
})
export class ProductDetailsPage {
  route = inject(ActivatedRoute);
  store = inject(ProductDetailsStore);
  publishStore = inject(ProductPublishStore);
  alerts = inject(AppAlerts);
  vxSwiper = viewChild(VxProductSwiperComponent, { read: VxProductSwiperComponent });
  swiperActiveIndex = signal(0);
  productId: string = this.route.snapshot.params['productId'];
  eventBus = inject(NgEventBus);
  notificationStore = inject(NotificationsStore);
  currentShareUrl = signal('');
  private readonly baseConfig = inject(BASE_CONFIG);
  shareBaseUrl = this.baseConfig.shareBaseUrl;
  shareModalOpen = signal(false);

  onAttributeToggle(attrValue: Entity.VariationAttribute) {
    this.store.toggleAttribute(attrValue);
  }

  constructor() {
    this.store.getProductById({ id: this.productId });

    this.eventBus.on<{ productId: string }>('product:fetch').subscribe((data) => {
      if (data.data?.productId === this.productId) {
        this.store.getProductById({ id: this.productId });
      }
    });

    effect(() => {
      const variant = this.store.selectedVariation();
      if (variant?.image) {
        const index = this.store.dynamicInfo().images.findIndex((x) => x === variant.image);
        if (index !== -1) {
          this.vxSwiper()?.slideTo(index);
        }
      }
    });

    effect(() => {
      const state = this.publishStore.requestToPublishProductState();
      if (state.success) {
        this.alerts.successSnackbarTR('PRODUCTS.PRODUCT_SENT_FOR_REVIEW');
        untracked(() => {
          this.publishStore.resetRequestToPublishProductState();
          this.store.getProductById({ id: this.productId });
        });
      }
    });

    effect(() => {
      const state = this.publishStore.unPublishProductState();
      if (state.success) {
        this.alerts.successSnackbarTR('PRODUCTS.PRODUCT_UNPUBLISHED');
        untracked(() => {
          this.publishStore.resetUnPublishProductState();
          this.store.getProductById({ id: this.productId });
        });
      }
    });
  }

  async openShareOptions(productId: string) {
    if (this.store.product()?.publishStatus === 'published') {
      const shareUrl = `${this.shareBaseUrl}/p/${productId}`;
      this.currentShareUrl.set(shareUrl);
      this.openShareModal();
    } else {
      this.alerts.warningSnackbarTR('PRODUCTS.PRODUCT_NOT_PUBLISHED');
    }
  }

  openShareModal() {
    this.shareModalOpen.set(true);
  }

  async sendForReview(productId: string) {
    if (!this.notificationStore.isGranted()) {
      await this.notificationStore.requestPermission({
        showAlert: true,
        trMessage: 'NOTIFICATIONS.REQUEST_PERMISSION_FOR_PRODUCT_PUBLISH',
      });
    }
    this.publishStore.requestToPublishProduct({ productId });
  }

  async shareToSocialMedia(platform: string, url: string) {
    // Input validation
    if (!platform || !url) {
      this.alerts.snackbarTR('COMMON.INVALID_SHARE_PARAMETERS');
      return;
    }

    // Validate URL format
    try {
      new URL(url);
    } catch {
      this.alerts.snackbarTR('COMMON.INVALID_URL');
      return;
    }

    const product = this.store.product();
    if (!product) {
      this.alerts.snackbarTR('COMMON.PRODUCT_NOT_FOUND');
      return;
    }

    // Check if Web Share API is available (mobile optimization)
    if (navigator.share && platform === 'native') {
      try {
        await navigator.share({
          title: product.name,
          text: product.description || '',
          url: url,
        });
        return;
      } catch (error) {
        // User cancelled or error occurred, fall back to URL sharing
        console.warn('Web Share API failed:', error);
      }
      return;
    }

    const encodedUrl = encodeURIComponent(url);
    const title = encodeURIComponent(product.name || '');
    const description = encodeURIComponent(product.description || '');

    let shareUrl = '';

    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case 'twitter': {
        // X/Twitter - properly format text with spacing
        const tweetText = description ? `${title} - ${description}` : title;
        shareUrl = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodeURIComponent(tweetText)}`;
        break;
      }
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(`${product.name} ${url}`)}`;
        break;
      case 'telegram':
        shareUrl = `https://t.me/share/url?url=${encodedUrl}&text=${title}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
        break;
      default:
        this.alerts.snackbarTR('COMMON.UNSUPPORTED_PLATFORM');
        return;
    }

    if (shareUrl) {
      try {
        const newWindow = window.open(shareUrl, '_blank', 'noopener,noreferrer');
        if (!newWindow) {
          // Popup blocked - provide fallback
          this.copyToClipboard(url);
          this.alerts.snackbarTR('COMMON.POPUP_BLOCKED_LINK_COPIED');
        }
      } catch (error) {
        console.error('Failed to open share window:', error);
        // Fallback to copying link
        this.copyToClipboard(url);
        this.alerts.snackbarTR('COMMON.SHARE_FAILED_LINK_COPIED');
      }
    }
  }
  copyToClipboard(text: string) {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        this.alerts.successSnackbarTR('COMMON.LINK_COPIED', { duration: 2000 });
      })
      .catch(() => {
        this.alerts.snackbarTR('COMMON.COPY_FAILED', { duration: 2000 });
      });
  }

  addToOrder() {
    this.eventBus.cast('product:addToOrder', {
      product: this.store.product(),
      variation: this.store.selectedVariation(),
    });
  }
}
